import ReactEChartsCore from "echarts-for-react/lib/core";
import { use } from "echarts/core";
import { useAppContext } from "providers/AppProvider";
import { LineChart } from "echarts/charts";
import { CanvasRenderer } from "echarts/renderers";
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
  ToolboxComponent,
  TitleComponent,
} from "echarts/components";
import { CallbackDataParams } from "echarts/types/dist/shared";
import { useQuery } from "@tanstack/react-query";
import axios, { AxiosError } from "axios";
import { usePermissionModal } from "contexts/PermissionModalContext";
import * as echarts from 'echarts/core';
import { DashCard, DashCardBody } from "components/modules/dashboard/v2/DashCard";
import { formatCurrency } from "utils/formatters";

const formatAxisCurrency = (value: number) =>
  `R$ ${new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 0 }).format(value)}`;

// Register all required components
use([
  LineChart as any,
  CanvasRenderer,
  GridComponent,
  TooltipComponent,
  LegendComponent,
  ToolboxComponent,
  TitleComponent,
]);

interface SalesEvolutionChartProps {
  period: "annual" | "semiannual" | "monthly";
}

// Interface para os dados retornados pela API
interface SalesEvolutionChartData {
  data: {
    labels: string[];
    values: number[];
    comparisonValues: number[];
    growthPercentage: number;
    isPositiveGrowth: boolean;
  };
}

// Mapeamento dos períodos do front para os períodos aceitos pelo backend
const periodMap = {
  annual: "year",
  semiannual: "semester",
  monthly: "month",
};

// Cores da série ligadas aos tokens de .dash-v2 (ver _dashboard-v2.scss) — ECharts renderiza
// em canvas e não resolve var(--token), por isso os pares light/dark ficam espelhados aqui.
const SERIES_COLORS = {
  light: { atual: "#3874FF", anterior: "#C96A3C" },
  dark: { atual: "#85A9FF", anterior: "#E79468" },
};

const SalesEvolutionChart = ({ period }: SalesEvolutionChartProps) => {
  const { getThemeColor, config } = useAppContext();
  const seriesColors = config.theme === "dark" ? SERIES_COLORS.dark : SERIES_COLORS.light;

  const fetchSalesEvolutionData = async (): Promise<SalesEvolutionChartData> => {
    const mappedPeriod = periodMap[period];
    const response = await axios.get<SalesEvolutionChartData>(
      `/api/SalesArea/sales-evolution?period=${mappedPeriod}`
    );
    return response.data;
  };

  const { getQueryRetryHandler } = usePermissionModal();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["salesEvolutionChart", period],
    queryFn: fetchSalesEvolutionData,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: getQueryRetryHandler({
      queryKey: ["salesEvolutionChart", period],
      defaultMessage: "Você não possui permissão para visualizar a evolução de vendas.",
      showModal: false,
    }),
  });

  const getChartData = (inputData: SalesEvolutionChartData | null = null) => {
    const d = inputData?.data || data?.data || {
      labels: [],
      values: [],
      comparisonValues: [],
      growthPercentage: 0,
      isPositiveGrowth: false,
    };

    return {
      dates: d.labels,
      salesData: d.values,
      previousPeriodData: d.comparisonValues,
      growthPercentage: d.growthPercentage,
      isPositiveGrowth: d.isPositiveGrowth,
    };
  };

  const getOptions = () => {
    const { dates, salesData, previousPeriodData } = getChartData();

    return {
      tooltip: {
        trigger: "axis",
        padding: [7, 10],
        backgroundColor: getThemeColor("body-highlight-bg"),
        borderColor: getThemeColor("border-color"),
        textStyle: { color: getThemeColor("light-text-emphasis") },
        borderWidth: 1,
        transitionDuration: 0,
        formatter: (params: CallbackDataParams[]) => {
          let tooltipContent = `<div class="fw-semibold">${params[0].name}</div>`;

          params.forEach((param) => {
            const seriesName = param.seriesName;
            const value = formatCurrency(Number(param.value) || 0);
            const color = param.color;

            tooltipContent += `<div class="d-flex align-items-center mt-2">
              <div style="height:8px;width:8px;border-radius:50%;background:${color};margin-right:8px;"></div>
              <div>${seriesName}: <span class="fw-semibold">${value}</span></div>
            </div>`;
          });

          return tooltipContent;
        },
      },
      legend: {
        data: ["Vendas Atuais", "Período Anterior"],
        icon: "circle",
        top: 0,
        textStyle: {
          color: getThemeColor("body-color"),
        },
      },
      xAxis: {
        type: "category",
        data: dates,
        boundaryGap: false,
        axisLine: {
          lineStyle: {
            color: getThemeColor("border-color"),
          },
        },
        axisTick: {
          show: false,
        },
        axisLabel: {
          color: getThemeColor("secondary-color"),
          margin: 15,
          formatter: (value: string) => value,
        },
        splitLine: {
          show: false,
        },
      },
      yAxis: {
        type: "value",
        splitLine: {
          lineStyle: {
            type: "dashed",
            color: getThemeColor("border-color"),
          },
        },
        axisLabel: {
          color: getThemeColor("secondary-color"),
          margin: 15,
          formatter: (value: number) => formatAxisCurrency(value),
        },
      },
      series: [
        {
          name: "Vendas Atuais",
          type: "line",
          data: salesData,
          lineStyle: {
            width: 3,
            color: seriesColors.atual,
          },
          symbol: "circle",
          symbolSize: 8,
          itemStyle: {
            color: seriesColors.atual,
          },
          showSymbol: false,
          smooth: true,
        },
        {
          name: "Período Anterior",
          type: "line",
          data: previousPeriodData,
          lineStyle: {
            width: 3,
            type: "dashed",
            color: seriesColors.anterior,
          },
          symbol: "circle",
          symbolSize: 8,
          itemStyle: {
            color: seriesColors.anterior,
          },
          showSymbol: false,
          smooth: true,
        },
      ],
      grid: {
        left: 60,
        right: 20,
        bottom: 60,
        top: 50,
        containLabel: true,
      },
    };
  };

  const isAccessDenied = axios.isAxiosError(error) && error?.response?.status === 403;
  if (isAccessDenied) return null;

  if (isLoading) {
    return (
      <DashCard hoverable={false}>
        <DashCardBody>
          <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
            <div className="dash-skeleton" style={{ height: 10, width: "100%" }} />
            <div className="dash-skeleton" style={{ height: 10, width: "100%" }} />
            <div className="dash-skeleton" style={{ height: 10, width: "100%" }} />
          </div>
        </DashCardBody>
      </DashCard>
    );
  }

  if (isError) {
    return (
      <DashCard hoverable={false}>
        <DashCardBody>
          <div className="dash-empty">
            <h6>Erro ao carregar dados</h6>
            <p>Não foi possível obter os dados de evolução de vendas.</p>
          </div>
        </DashCardBody>
      </DashCard>
    );
  }

  return (
    <DashCard hoverable={false} className="h-100">
      <div className="dash-card-body--flush h-100">
        <ReactEChartsCore
          echarts={echarts}
          option={getOptions()}
          style={{ height: "100%", width: "100%" }}
          notMerge={true}
          lazyUpdate={false}
        />
      </div>
    </DashCard>
  );
};

export default SalesEvolutionChart;
